import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";

interface Props {
  blockId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UnblockAlertDialog(props: Props) {
  const leastDestructiveRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <AlertDialog
      isOpen={props.isOpen}
      leastDestructiveRef={leastDestructiveRef}
      onClose={props.onClose}
    >
      <AlertDialogOverlay>
        <AlertContent leastDestructiveRef={leastDestructiveRef} {...props} />
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function AlertContent(props: Props & { leastDestructiveRef: React.RefObject<HTMLButtonElement> }) {
  const { api } = useApi();
  const toast = useToast();
  const [note, setNote] = React.useState("");

  const unblock = useMutation({
    mutationFn: (note: string) =>
      api.post("./rn_performance_blocks/:blockId/unblock", {
        path: { blockId: props.blockId },
        body: { note },
      }),
    onSuccess: () => {
      props.onSuccess();
      props.onClose();
      toast({
        title: "Caregiver unblocked",
        description: "The performance block has been removed successfully.",
        status: "success",
        position: "top-right",
      });
    },
    onError: (error) => {
      toast({
        title: "Error unblocking caregiver",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  return (
    <AlertDialogContent>
      <AlertDialogHeader fontSize="lg" fontWeight="bold">
        Unblock Caregiver
      </AlertDialogHeader>

      <AlertDialogBody>
        Are you sure you want to unblock this caregiver? This will allow them to accept visits
        again.
        <FormControl isRequired mt={4}>
          <FormLabel>Note</FormLabel>
          <Textarea
            placeholder="Enter reason for unblocking..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormControl>
      </AlertDialogBody>

      <AlertDialogFooter>
        <Button ref={props.leastDestructiveRef} onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          isDisabled={note.trim().length === 0}
          isLoading={unblock.isPending}
          ml={3}
          onClick={() => unblock.mutate(note)}
        >
          Unblock
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}
